import type { OpenPencilFigmaSourceBundle } from "./openpencil-figma-source";
import type { OpenPencilSourceWorkspace } from "./openpencil-source-workspace";

export type OpenPencilSourceRoundtripStatus =
  | "blocked"
  | "source-retained"
  | "revision-pending"
  | "verified";

export interface OpenPencilSourceRoundtripProof {
  status: OpenPencilSourceRoundtripStatus;
  sourceRevision: number | null;
  parentSha256: string | null;
  currentSha256: string | null;
  artifactRef: string | null;
  evidenceRefs: string[];
  blockers: string[];
  claimAllowed: boolean;
  summary: string;
}

export function buildOpenPencilSourceRoundtripProof(
  workspace: OpenPencilSourceWorkspace | null,
  sourceBundle?: OpenPencilFigmaSourceBundle | null,
): OpenPencilSourceRoundtripProof {
  const bundle = workspace?.bundle ?? sourceBundle;
  const sourceRevision =
    workspace?.revision ?? bundle?.revisionLineage?.sourceRevision ?? null;
  if (!bundle?.fileSha256 || !bundle.sourceArtifactRef) {
    return blocked(
      sourceRevision,
      bundle?.revisionLineage?.parentSha256 ?? null,
      bundle?.fileSha256 ?? null,
      bundle?.sourceArtifactRef ?? null,
      [
        "The saved Figma source needs both a file reference and a SHA-256 checksum.",
      ],
    );
  }
  const lineage = bundle.revisionLineage;
  if (!lineage) {
    return {
      status: "source-retained",
      sourceRevision,
      parentSha256: null,
      currentSha256: bundle.fileSha256,
      artifactRef: bundle.sourceArtifactRef,
      evidenceRefs: [bundle.sourceArtifactRef],
      blockers: [],
      claimAllowed: true,
      summary:
        "The original saved Figma source can be exported and checked against its checksum.",
    };
  }
  if (lineage.parentSha256 === bundle.fileSha256) {
    return blocked(
      sourceRevision,
      lineage.parentSha256,
      bundle.fileSha256,
      bundle.sourceArtifactRef,
      ["The edited revision has the same hash as its parent source."],
    );
  }
  const diff = workspace?.revisionVisualDiff;
  if (
    diff?.status === "approved" &&
    diff.blockers.length === 0 &&
    diff.evidenceRefs.length > 0 &&
    diff.sourceRevision === lineage.sourceRevision
  ) {
    return {
      status: "verified",
      sourceRevision,
      parentSha256: lineage.parentSha256,
      currentSha256: bundle.fileSha256,
      artifactRef: bundle.sourceArtifactRef,
      evidenceRefs: Array.from(
        new Set([
          lineage.evidenceRef,
          bundle.sourceArtifactRef,
          ...diff.evidenceRefs,
        ]),
      ),
      blockers: [],
      claimAllowed: true,
      summary: `Figma revision ${lineage.sourceRevision} export and visual delta are verified.`,
    };
  }
  const blockers = [
    ...(diff?.blockers ?? []),
    diff
      ? `Revision visual diff is ${diff.status}; approval evidence is required.`
      : "Revision visual diff has not been recorded.",
  ];
  return {
    status: "revision-pending",
    sourceRevision,
    parentSha256: lineage.parentSha256,
    currentSha256: bundle.fileSha256,
    artifactRef: bundle.sourceArtifactRef,
    evidenceRefs: [lineage.evidenceRef, bundle.sourceArtifactRef],
    blockers: Array.from(new Set(blockers)),
    claimAllowed: false,
    summary: blockers[0],
  };
}

function blocked(
  sourceRevision: number | null,
  parentSha256: string | null,
  currentSha256: string | null,
  artifactRef: string | null,
  blockers: string[],
): OpenPencilSourceRoundtripProof {
  return {
    status: "blocked",
    sourceRevision,
    parentSha256,
    currentSha256,
    artifactRef,
    evidenceRefs: [],
    blockers,
    claimAllowed: false,
    summary: blockers[0],
  };
}
